import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { ProgressBar, ColorDot, colorLabel } from "../components/ProgressBar";
import { TypeBadge } from "./Signalements";
import { toast, Toaster } from "sonner";
import { ArrowLeft, SlidersHorizontal, RotateCcw, FileSpreadsheet } from "lucide-react";
import { downloadBlob } from "../lib/download";

const inputCls = "w-full px-3 py-2 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none text-sm";

const EMPTY_FILTERS = { start: "", end: "", class_id: "", student_id: "", teacher_id: "", subject_id: "" };

function Stat({ label, value, color, testId }) {
  return (
    <div className="bg-white border border-[var(--parchment-2)] p-5" data-testid={testId}>
      <div className="eyebrow">{label}</div>
      <div className="mt-2 font-serif-display text-3xl font-semibold" style={color ? { color } : {}}>{value}</div>
    </div>
  );
}

function SyntheseHeader({ exporting, onExport }) {
  return (
    <div className="mt-6 flex flex-wrap items-end justify-between gap-6 pb-8 border-b border-[var(--parchment-2)]">
      <div>
        <div className="eyebrow">Direction · Synthèse filtrée</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl font-semibold mt-2">Synthèse & filtres</h1>
        <p className="mt-3 text-[var(--ink-soft)] max-w-2xl">
          Croisez les données de l'établissement : assiduité, signalements et progression des programmes,
          filtrés par période, élève, classe, professeur et matière.
        </p>
      </div>
      <button
        onClick={onExport}
        disabled={exporting}
        className="btn-secondary !py-2.5 !px-5 text-sm flex items-center gap-2"
        title="Exporte la synthèse filtrée (stats, assiduité, signalements, progression) en Excel"
        data-testid="synthese-export-btn"
      >
        <FileSpreadsheet size={16} /> {exporting ? "Export..." : "Exporter (Excel)"}
      </button>
    </div>
  );
}

function PeriodChips({ periods, filters, setFilters }) {
  return (
    <div className="flex flex-wrap gap-2 mb-4" data-testid="synthese-period-chips">
      {periods.map((p) => (
        <button
          key={p.id || p.key}
          type="button"
          onClick={() => setFilters((f) => ({ ...f, start: p.start, end: p.end }))}
          className={`px-3 py-1.5 text-xs font-medium border transition ${
            filters.start === p.start && filters.end === p.end
              ? "bg-[var(--maroon)] text-white border-[var(--maroon)]"
              : "bg-white text-[var(--ink-soft)] border-[var(--parchment-2)] hover:border-[var(--ink)]"
          }`}
          data-testid={`synthese-period-${p.key}`}
        >
          {p.name}
        </button>
      ))}
    </div>
  );
}

function FiltersCard({ filters, setFilters, setF, periods, classes, students, teachers, subjects }) {
  return (
    <div className="mt-8 bg-white border border-[var(--parchment-2)] p-6" data-testid="synthese-filters">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="eyebrow flex items-center gap-2"><SlidersHorizontal size={14} /> Filtres</div>
        <button className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5" onClick={() => setFilters(EMPTY_FILTERS)} data-testid="synthese-reset-btn">
          <RotateCcw size={13} /> Réinitialiser
        </button>
      </div>
      <PeriodChips periods={periods} filters={filters} setFilters={setFilters} />
      <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div>
          <label className="eyebrow block mb-1">Du</label>
          <input type="date" className={inputCls} value={filters.start} onChange={setF("start")} data-testid="synthese-start-input" />
        </div>
        <div>
          <label className="eyebrow block mb-1">Au</label>
          <input type="date" className={inputCls} value={filters.end} onChange={setF("end")} data-testid="synthese-end-input" />
        </div>
        <div>
          <label className="eyebrow block mb-1">Classe</label>
          <select className={inputCls} value={filters.class_id} onChange={setF("class_id")} data-testid="synthese-class-select">
            <option value="">Toutes</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="eyebrow block mb-1">Élève</label>
          <select className={inputCls} value={filters.student_id} onChange={setF("student_id")} disabled={!filters.class_id} data-testid="synthese-student-select">
            <option value="">{filters.class_id ? "Tous" : "Choisir une classe"}</option>
            {students.map((s) => <option key={s.id} value={s.id}>{s.last_name.toUpperCase()} {s.first_name}</option>)}
          </select>
        </div>
        <div>
          <label className="eyebrow block mb-1">Professeur</label>
          <select className={inputCls} value={filters.teacher_id} onChange={setF("teacher_id")} data-testid="synthese-teacher-select">
            <option value="">Tous</option>
            {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label className="eyebrow block mb-1">Matière</label>
          <select className={inputCls} value={filters.subject_id} onChange={setF("subject_id")} data-testid="synthese-subject-select">
            <option value="">Toutes</option>
            {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}

function SyntheseStats({ appel, incidents }) {
  return (
    <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-4">
      <Stat label="Absences (créneaux)" value={appel.total_absences} color="var(--late)" testId="synthese-stat-absences" />
      <Stat label="dont justifiées" value={appel.total_absences_just} color="var(--ok)" testId="synthese-stat-just" />
      <Stat label="Jours d'absence" value={appel.total_absence_days} testId="synthese-stat-days" />
      <Stat label="Retards" value={appel.total_retards} color="var(--just)" testId="synthese-stat-retards" />
      <Stat label="Signalements" value={incidents.total} color="var(--maroon)" testId="synthese-stat-incidents" />
    </div>
  );
}

function ProgressionSection({ progression }) {
  return (
    <section className="mt-10">
      <h2 className="font-serif-display text-2xl font-semibold">Progression des programmes ({progression.length})</h2>
      <div className="mt-4 bg-white border border-[var(--parchment-2)] divide-y divide-[var(--parchment-2)]" data-testid="synthese-progression">
        {progression.map((r) => (
          <Link
            key={r.assignment_id}
            to={`/admin/assignment/${r.assignment_id}`}
            className="grid sm:grid-cols-[1fr_1fr_auto] gap-4 items-center px-5 py-4 hover:bg-[var(--parchment)]/40 transition"
            data-testid={`synthese-progress-row-${r.assignment_id}`}
          >
            <div>
              <div className="font-medium">{r.subject?.name} · {r.class?.name}</div>
              <div className="text-xs text-[var(--ink-soft)] mt-0.5">{r.teacher?.name}</div>
            </div>
            <ProgressBar percent={r.progress.percent} expectedPercent={r.progress.expected_percent} color={r.progress.color} />
            <div className="flex items-center gap-2 text-xs">
              <ColorDot color={r.progress.color} />
              <span>{colorLabel(r.progress.color)} · {r.progress.ended}/{r.progress.total} ch.</span>
            </div>
          </Link>
        ))}
        {progression.length === 0 && <div className="px-5 py-6 text-sm text-[var(--ink-soft)] italic">Aucun programme ne correspond aux filtres.</div>}
      </div>
    </section>
  );
}

function AssiduiteSection({ byStudent }) {
  return (
    <section className="bg-white border border-[var(--parchment-2)] p-6" data-testid="synthese-assiduite">
      <h2 className="font-serif-display text-2xl font-semibold">Assiduité par élève</h2>
      <table className="w-full text-sm mt-4">
        <thead>
          <tr className="border-b border-[var(--parchment-2)] text-left">
            <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium">Élève</th>
            <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium text-center">Abs. (créneaux)</th>
            <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium text-center">Just.</th>
            <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium text-center">Abs. (jours)</th>
            <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium text-center">Retards</th>
          </tr>
        </thead>
        <tbody>
          {byStudent.map((r) => (
            <tr key={r.student.id} className="border-b border-[var(--parchment-2)]/60">
              <td className="py-2.5">
                {r.student.last_name.toUpperCase()} {r.student.first_name}
                <span className="text-xs text-[var(--ink-soft)] ml-2">{r.class?.name}</span>
              </td>
              <td className="py-2.5 text-center font-mono-spec text-[var(--late)]">{r.absences_count}</td>
              <td className="py-2.5 text-center font-mono-spec text-[var(--ok)]">{r.absences_just_count}</td>
              <td className="py-2.5 text-center font-mono-spec">{r.absences_days_count}</td>
              <td className="py-2.5 text-center font-mono-spec text-[var(--just)]">{r.retards_count}</td>
            </tr>
          ))}
          {byStudent.length === 0 && (
            <tr><td colSpan={5} className="py-4 text-sm text-[var(--ink-soft)] italic">Aucune absence ni retard sur cette sélection.</td></tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

function IncidentsSection({ incidents }) {
  return (
    <section className="bg-white border border-[var(--parchment-2)] p-6" data-testid="synthese-incidents">
      <h2 className="font-serif-display text-2xl font-semibold">Signalements ({incidents.total})</h2>
      <div className="mt-4 space-y-3 max-h-[500px] overflow-y-auto">
        {incidents.items.map((r) => (
          <div key={r.id} className="border border-[var(--parchment-2)] p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{r.student?.last_name.toUpperCase()} {r.student?.first_name}</span>
              <span className="text-[var(--ink-soft)]">{r.class?.name}</span>
              <TypeBadge type={r.type} label={r.type_label} />
            </div>
            <div className="mt-1 text-xs text-[var(--muted)] font-mono-spec">
              {r.date} · {r.subject?.name || "—"} · signalé par {r.reported_by_user?.name || "—"}
            </div>
            {r.comment && <div className="mt-1 text-xs text-[var(--ink-soft)]">{r.comment}</div>}
          </div>
        ))}
        {incidents.items.length === 0 && <div className="py-4 text-sm text-[var(--ink-soft)] italic">Aucun signalement sur cette sélection.</div>}
      </div>
    </section>
  );
}

function useSyntheseData() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [periods, setPeriods] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/periods"), api.get("/classes"), api.get("/admin/teachers"), api.get("/subjects")]).then(
      ([p, c, t, s]) => {
        setPeriods(p.data);
        setClasses(c.data);
        setTeachers(t.data);
        setSubjects(s.data);
      }
    );
  }, []);

  useEffect(() => {
    if (!filters.class_id) {
      setStudents([]);
      return;
    }
    api.get(`/classes/${filters.class_id}/students`).then(({ data }) => setStudents(data));
  }, [filters.class_id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await api.get("/admin/synthese", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const setF = (k) => (e) => {
    const v = e.target.value;
    setFilters((f) => (k === "class_id" ? { ...f, class_id: v, student_id: "" } : { ...f, [k]: v }));
  };

  return { filters, setFilters, setF, periods, classes, students, teachers, subjects, data, loading };
}

export default function AdminSynthese() {
  const { filters, setFilters, setF, periods, classes, students, teachers, subjects, data, loading } = useSyntheseData();
  const [exporting, setExporting] = useState(false);

  const exportXlsx = async () => {
    setExporting(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const ok = await downloadBlob("/admin/synthese/export.xlsx", "synthese-lafayette.xlsx", "Export Excel de la synthèse téléchargé", "Échec de l'export Excel", params);
    setExporting(false);
    return ok;
  };

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <AppHeader />
      <Toaster richColors position="top-right" />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--maroon)] transition-colors" data-testid="synthese-back">
          <ArrowLeft size={16} /> Retour au tableau de bord
        </Link>

        <SyntheseHeader exporting={exporting} onExport={exportXlsx} />

        <FiltersCard
          filters={filters}
          setFilters={setFilters}
          setF={setF}
          periods={periods}
          classes={classes}
          students={students}
          teachers={teachers}
          subjects={subjects}
        />

        {loading || !data ? (
          <div className="mt-8 text-[var(--ink-soft)]">Chargement...</div>
        ) : (
          <>
            <SyntheseStats appel={data.appel} incidents={data.incidents} />
            <ProgressionSection progression={data.progression} />
            <div className="mt-10 grid lg:grid-cols-2 gap-6">
              <AssiduiteSection byStudent={data.appel.by_student} />
              <IncidentsSection incidents={data.incidents} />
            </div>
          </>
        )}
      </main>
    </div>
  );
}
